/**
 * GanttAcumulado
 * Pantalla que muestra el Gantt acumulado de todos los proyectos o proyectos seleccionados
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { proyectoService } from '../services/onebaseService';
import eventosService from '../services/eventosService';
import eventosGanttService from '../services/eventosGanttService';
import Select from 'react-select';
import GanttChart from '../components/GanttChart';
import Loading from '../components/Loading';
import './AvanceGastarAcumulado.css'; // Reutilizamos los estilos

const GanttAcumulado = () => {
  const [loading, setLoading] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [proyectosSeleccionados, setProyectosSeleccionados] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [eventosSeleccionados, setEventosSeleccionados] = useState([]);
  const [actividadesAcumuladas, setActividadesAcumuladas] = useState([]);
  const [tareasAcumuladas, setTareasAcumuladas] = useState([]);
  const [loadingGantt, setLoadingGantt] = useState(false);

  useEffect(() => {
    cargarProyectos();
  }, []);

  // Cargar eventos cuando se carguen los proyectos
  useEffect(() => {
    if (proyectos.length > 0) {
      cargarEventos();
    }
  }, [proyectos]);

  const cargarProyectos = async () => {
    try {
      // Solo cargar proyectos en ejecución
      const response = await proyectoService.getAll({
        status: 'activo',
        estado: 'en_ejecucion'
      });
      const proyectosData = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      setProyectos(proyectosData);
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
      toast.error('Error al cargar proyectos');
    }
  };

  const cargarEventos = async (proyectoIds = []) => {
    setLoading(true);
    try {
      const params = {};

      if (proyectoIds.length > 0) {
        // Si hay proyectos seleccionados, filtrar por ellos
        params.proyecto__in = proyectoIds.join(',');
        console.log('🔍 Filtrando eventos por proyectos:', proyectoIds);
      } else {
        // Si no hay proyectos seleccionados, cargar eventos de todos los proyectos activos en ejecución
        const proyectosActivos = proyectos
          .filter(p => p.status === 'activo' && p.estado === 'en_ejecucion')
          .map(p => p.id);

        if (proyectosActivos.length > 0) {
          params.proyecto__in = proyectosActivos.join(',');
          console.log('🔍 Cargando eventos de proyectos activos:', proyectosActivos);
        }
      }

      const response = await eventosService.getEventos(params);
      const eventosData = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);

      console.log('📅 Eventos cargados:', eventosData.length);
      console.log('📅 Datos de eventos:', eventosData);
      setEventos(eventosData);

      // Limpiar la selección de eventos cuando se filtran proyectos
      setEventosSeleccionados([]);
    } catch (error) {
      console.error('Error al cargar eventos:', error);
      toast.error('Error al cargar eventos');
      setEventos([]);
      setEventosSeleccionados([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProyectosChange = (selectedOptions) => {
    setProyectosSeleccionados(selectedOptions || []);
    const proyectoIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    cargarEventos(proyectoIds);
  };

  const handleEventosChange = (selectedOptions) => {
    setEventosSeleccionados(selectedOptions || []);
  };

  // Cargar todas las actividades y tareas de los eventos seleccionados
  const cargarDatosGanttAcumulados = async () => {
    if (!eventosSeleccionados || eventosSeleccionados.length === 0) {
      setActividadesAcumuladas([]);
      setTareasAcumuladas([]);
      return;
    }

    setLoadingGantt(true);
    try {
      const todasActividades = [];
      const todasTareas = [];

      // Cargar actividades y tareas de cada evento seleccionado
      for (const eventoOption of eventosSeleccionados) {
        const eventoId = eventoOption.value;

        // Buscar el evento completo para obtener información del proyecto
        const eventoCompleto = eventos.find(e => e.id === eventoId);
        const proyectoNombre = eventoCompleto?.proyecto_nombre || 'Sin proyecto';
        const proyectoId = eventoCompleto?.proyecto || null;

        try {
          // Cargar actividades del evento
          const actividadesResponse = await eventosGanttService.actividades.list(eventoId);
          const actividadesData = Array.isArray(actividadesResponse.data)
            ? actividadesResponse.data
            : (actividadesResponse.data.results || []);

          // Agregar información del evento y proyecto a cada actividad para identificación
          const actividadesConEvento = actividadesData.map(act => ({
            ...act,
            evento_id: eventoId,
            evento_nombre: eventoOption.label,
            proyecto_id: proyectoId,
            proyecto_nombre: proyectoNombre
          }));
          todasActividades.push(...actividadesConEvento);

          // Cargar tareas del evento
          const tareasResponse = await eventosGanttService.tareas.listByEvento(eventoId);
          const tareasData = Array.isArray(tareasResponse.data)
            ? tareasResponse.data
            : (tareasResponse.data.results || []);

          // Agregar información del evento y proyecto a cada tarea
          const tareasConEvento = tareasData.map(tarea => ({
            ...tarea,
            evento_id: eventoId,
            evento_nombre: eventoOption.label,
            proyecto_id: proyectoId,
            proyecto_nombre: proyectoNombre
          }));
          todasTareas.push(...tareasConEvento);
        } catch (error) {
          console.error(`Error al cargar datos del evento ${eventoId}:`, error);
        }
      }

      console.log('📊 Actividades acumuladas:', todasActividades.length);
      console.log('📋 Tareas acumuladas:', todasTareas.length);

      setActividadesAcumuladas(todasActividades);
      setTareasAcumuladas(todasTareas);
    } catch (error) {
      console.error('Error al cargar datos del Gantt acumulado:', error);
      toast.error('Error al cargar datos del cronograma');
    } finally {
      setLoadingGantt(false);
    }
  };

  // Cargar datos del Gantt cuando cambien los eventos seleccionados
  useEffect(() => {
    cargarDatosGanttAcumulados();
  }, [eventosSeleccionados]);

  // Opciones para el selector de proyectos
  const proyectosOptions = proyectos.map(proyecto => ({
    value: proyecto.id,
    label: `${proyecto.cliente_nombre} - ${proyecto.nombre}`
  }));

  // Opciones para el selector de eventos
  const eventosOptions = eventos.map(evento => ({
    value: evento.id,
    label: `${evento.title} ${evento.proyecto_nombre ? `(${evento.proyecto_nombre})` : ''}`
  }));

  if (loading && eventos.length === 0) {
    return (
      <div className="avance-acumulado-page">
        <Loading fullScreen={false} mensaje="Cargando información" />
      </div>
    );
  }

  return (
    <div className="avance-acumulado-page">
      {/* Header */}
      <div className="page-header">
        <h1>📅 Gantt Acumulado</h1>
        <div className="header-info">
          {eventos.length > 0 && (
            <span className="info-badge">
              {eventos.length} {eventos.length === 1 ? 'evento' : 'eventos'}
            </span>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-container" style={{
        marginBottom: '2rem',
        display: 'flex',
        gap: '1rem',
        alignItems: 'flex-start'
      }}>
        <div className="filtro-proyectos" style={{ flex: 1 }}>
          <label>Seleccionar Proyectos (opcional):</label>
          <Select
            isMulti
            options={proyectosOptions}
            value={proyectosSeleccionados}
            onChange={handleProyectosChange}
            placeholder="Todos los proyectos"
            className="proyecto-select"
            classNamePrefix="select"
            isClearable
          />
        </div>

        <div className="filtro-proyectos" style={{ flex: 1 }}>
          <label>Seleccionar Eventos:</label>
          <Select
            isMulti
            options={eventosOptions}
            value={eventosSeleccionados}
            onChange={handleEventosChange}
            placeholder={loading ? "Cargando eventos..." : eventos.length > 0 ? "Seleccione uno o más eventos" : "No hay eventos disponibles"}
            className="proyecto-select"
            classNamePrefix="select"
            isClearable
            isDisabled={loading}
            noOptionsMessage={() => "No hay eventos disponibles"}
          />
        </div>
      </div>

      {/* Contenido principal */}
      <div className="avance-por-gastar-container">
        {loadingGantt ? (
          <Loading fullScreen={false} mensaje="Cargando cronograma acumulado" />
        ) : eventosSeleccionados && eventosSeleccionados.length > 0 ? (
          <div className="gantt-wrapper" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '1.3rem',
                color: '#1f2937',
                fontWeight: '600',
                marginBottom: '0.5rem'
              }}>
                📅 Gantt Acumulado
              </h3>
              <div style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                fontSize: '0.9rem',
                color: '#6b7280'
              }}>
                <span>
                  <strong>{eventosSeleccionados.length}</strong> {eventosSeleccionados.length === 1 ? 'evento' : 'eventos'} seleccionado{eventosSeleccionados.length === 1 ? '' : 's'}
                </span>
                <span>•</span>
                <span>
                  <strong>{actividadesAcumuladas.length}</strong> {actividadesAcumuladas.length === 1 ? 'actividad' : 'actividades'}
                </span>
                <span>•</span>
                <span>
                  <strong>{tareasAcumuladas.length}</strong> {tareasAcumuladas.length === 1 ? 'tarea' : 'tareas'}
                </span>
              </div>
              {eventosSeleccionados.length > 0 && (
                <div style={{
                  marginTop: '0.75rem',
                  fontSize: '0.85rem',
                  color: '#9ca3af'
                }}>
                  Eventos: {eventosSeleccionados.map(e => e.label).join(', ')}
                </div>
              )}
            </div>
            <GanttChart
              actividadesExternas={actividadesAcumuladas}
              tareasExternas={tareasAcumuladas}
            />
          </div>
        ) : (
          <div className="empty-state">
            <p>
              {eventos.length === 0
                ? 'No hay eventos disponibles'
                : 'Seleccione uno o más eventos para ver su Gantt acumulado'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GanttAcumulado;
